<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useUserStore } from "@/store/user-store";

// Asset class options
const assetClasses = [
  { label: "All", value: "all" },
  { label: "Equity", value: "Equity" },
  { label: "Debt/Fixed Income", value: "Debt" },
  { label: "Gold", value: "Gold" },
  { label: "Real Estate", value: "Real Estate" },
  { label: "International", value: "International" },
  { label: "Alternatives", value: "Alternatives" },
];

// Type filter options
const typeFilters = [
  { label: "All", value: "all" },
  { label: "Blogs", value: "blogs" },
  { label: "Videos", value: "videos" },
];

const userStore = useUserStore();

const loading = ref(true);
const items = ref([]);
const page = ref(1);
const pageSize = ref(12);
const hasMore = ref(false);

const selectedType = ref("all");
const selectedAssetClass = ref("all");
const searchQuery = ref("");

// Tag cloud
const tagCloud = ref([]);

// For skeletons
const skeletonCount = 6;

// Fetch data from store
const fetchData = async () => {
  loading.value = true;
  const payload = {
    action: "list",
    filters: {
      type: selectedType.value,
      assetClass: selectedAssetClass.value,
      q: searchQuery.value,
    },
    page: page.value,
    pageSize: pageSize.value,
  };
  try {
    const resp = await userStore.getBlogsVideos(payload);
    items.value = resp?.data?.items || [];
    hasMore.value = !!resp?.data?.hasMore;
    // Build tag cloud from all tags
    const tags = {};
    items.value.forEach((item) => {
      (item.tags || []).forEach((tag) => {
        tags[tag] = (tags[tag] || 0) + 1;
      });
    });
    tagCloud.value = Object.entries(tags)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([tag]) => tag);
  } finally {
    loading.value = false;
  }
};

onMounted(fetchData);
watch([selectedType, selectedAssetClass, searchQuery], () => {
  page.value = 1;
  fetchData();
});

// Filter handlers
const setType = (val) => (selectedType.value = val);
const setAssetClass = (val) => (selectedAssetClass.value = val);
const setSearch = (e) => {
  searchQuery.value = e.target.value;
};

const filteredItems = computed(() => items.value);

// Card badge color by type/asset
const badgeColor = (type) => {
  if (type === "video") return "var(--an-primary)";
  if (type === "blog") return "var(--an-success)";
  return "var(--an-gray)";
};

// Asset class pill color
const assetClassColor = (asset) => {
  switch (asset) {
    case "Equity":
      return "var(--an-primary)";
    case "Debt":
    case "Fixed Income":
      return "#6c757d";
    case "Gold":
      return "#e6b800";
    case "Real Estate":
      return "#b48a78";
    case "International":
      return "#3b82f6";
    case "Alternatives":
      return "#8e44ad";
    default:
      return "var(--an-gray)";
  }
};

// Lazy YouTube embed
const getYoutubeEmbed = (id) =>
  `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`;

// Accessibility: alt text for thumbnails
const getAlt = (item) =>
  item.type === "video"
    ? `YouTube video: ${item.title}`
    : `Blog: ${item.title}`;

// Skeleton array
const skeletonArr = computed(() => Array.from({ length: skeletonCount }));

// Responsive columns
const gridCols = computed(() => {
  if (window.innerWidth < 600) return 1;
  if (window.innerWidth < 900) return 2;
  return 3;
});
</script>

<template>
  <div class="blogs-videos-root">
    <!-- Concise Hero Banner -->
    <div class="bv-hero bv-hero-compact">
      <div class="bv-hero-content">
        <h1 class="bv-hero-title">Insights that turn decisions into outcomes.</h1>
        <div class="bv-hero-sub">
          Curated blogs & videos for smarter investing—across equity, debt, gold, real estate, and more.
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="bv-filters">
      <div class="bv-filter-group">
        <span class="bv-filter-label">Type:</span>
        <button
          v-for="f in typeFilters"
          :key="f.label"
          :class="['bv-pill', { active: selectedType === f.value }]"
          @click="setType(f.value)"
          :aria-pressed="selectedType === f.value"
        >{{ f.label }}</button>
      </div>
      <div class="bv-filter-group">
        <span class="bv-filter-label">Asset Class:</span>
        <button
          v-for="a in assetClasses"
          :key="a.label"
          :class="['bv-pill', { active: selectedAssetClass === a.value }]"
          @click="setAssetClass(a.value)"
          :aria-pressed="selectedAssetClass === a.value"
        >{{ a.label }}</button>
      </div>
      <div class="bv-search-group">
        <input
          class="bv-search"
          type="text"
          :placeholder="'Search by title or tag...'"
          v-model="searchQuery"
          aria-label="Search blogs and videos"
        />
      </div>
    </div>

    <!-- Tag Cloud -->
    <div v-if="tagCloud.length" class="bv-tag-cloud">
      <span class="bv-tag-label">Popular tags:</span>
      <span
        v-for="tag in tagCloud"
        :key="tag"
        class="bv-tag"
        tabindex="0"
        :aria-label="`Filter by tag ${tag}`"
        @click="searchQuery = tag"
      >{{ tag }}</span>
    </div>

    <!-- Content Grid -->
    <div
      class="bv-grid"
      :style="{
        gridTemplateColumns: `repeat(${gridCols}, 1fr)`
      }"
    >
      <!-- Loading skeletons -->
      <template v-if="loading">
        <div v-for="i in skeletonArr" :key="i" class="bv-card bv-skeleton">
          <div class="bv-thumb-skel shimmer"></div>
          <div class="bv-title-skel shimmer"></div>
          <div class="bv-line-skel shimmer"></div>
          <div class="bv-line-skel shimmer"></div>
        </div>
      </template>
      <!-- Empty state -->
      <template v-else-if="!filteredItems.length">
        <div class="bv-empty">
          <h2>Nothing to see here—yet.</h2>
          <p>Adjust filters or check back for fresh drops.</p>
        </div>
      </template>
      <!-- Cards -->
      <template v-else>
        <div
          v-for="item in filteredItems"
          :key="item.id"
          class="bv-card"
          tabindex="0"
          :aria-label="item.title"
        >
          <div class="bv-card-header">
            <span class="bv-badge" :style="{ background: badgeColor(item.type) }">
              {{ item.type === 'video' ? 'Video' : 'Blog' }}
            </span>
            <span
              v-if="item.assetClass"
              class="bv-pill asset"
              :style="{ background: assetClassColor(item.assetClass) }"
            >{{ item.assetClass }}</span>
          </div>
          <div class="bv-thumb-wrap">
            <iframe
              v-if="item.type === 'video' && item.youtubeId"
              class="bv-thumb"
              :src="getYoutubeEmbed(item.youtubeId)"
              :title="item.title"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
              loading="lazy"
            ></iframe>
            <img
              v-else
              class="bv-thumb"
              :src="item.thumbnailUrl"
              :alt="getAlt(item)"
              loading="lazy"
            />
          </div>
          <div class="bv-card-body">
            <div class="bv-card-title">{{ item.title }}</div>
            <div class="bv-card-desc">{{ item.description }}</div>
            <div class="bv-card-tags">
              <span
                v-for="tag in item.tags"
                :key="tag"
                class="bv-tag"
                tabindex="0"
                :aria-label="`Tag: ${tag}`"
                @click="searchQuery = tag"
              >{{ tag }}</span>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.blogs-videos-root {
  --primary: var(--an-primary, #0b545c);
  --surface: #f8fafc;
  --card: #fff;
  --border: #e2e8f0;
  --shadow: 0 2px 12px 0 rgba(0,0,0,0.07);
  --radius: 16px;
  --text: #1a2a32;
  --muted: #6c757d;
  --success: #1e7e34;
  --badge: #e3eafc;
  --pill: #e3eafc;
  --tag: #e3eafc;
  --tag-text: #0b545c;
  --empty: #f1f5f9;
  --shimmer: #e5e7eb;
  color: var(--text);
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  padding-bottom: 32px;
}

.bv-hero {
  background: linear-gradient(90deg, #e3f6f5 0%, #f8fafc 100%);
  border-radius: var(--radius);
  margin: 0 0 32px 0;
  padding: 32px 0 24px 0;
  box-shadow: var(--shadow);
  text-align: center;
}
.bv-hero-compact {
  padding: 12px 0 8px 0;
  margin-bottom: 12px;
}
.bv-hero-title {
  font-size: 2.2rem;
  font-weight: 700;
  color: var(--primary);
  margin-bottom: 8px;
}
.bv-hero-compact .bv-hero-title {
  font-size: 1.45rem;
  margin-bottom: 2px;
}
.bv-hero-sub {
  font-size: 1.15rem;
  color: var(--muted);
  margin-bottom: 8px;
}
.bv-hero-compact .bv-hero-sub {
  font-size: 1.02rem;
  margin-bottom: 0;
}
.bv-hero-support {
  font-size: 1rem;
  color: var(--muted);
  margin-bottom: 18px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.bv-hero-cta {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 8px;
}
.bv-btn {
  border-radius: 10px;
  padding: 12px 28px;
  font-size: 1.1rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
}
.bv-btn-primary {
  background: var(--primary);
  color: #fff;
}
.bv-btn-secondary {
  background: #e3eafc;
  color: var(--primary);
}
.bv-btn-primary:hover {
  background: #0a3c4c;
}
.bv-btn-secondary:hover {
  background: #d1e7dd;
}

.bv-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  align-items: center;
  margin-bottom: 18px;
  padding: 0 2px;
}
.bv-filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}
.bv-filter-label {
  font-weight: 600;
  color: var(--muted);
  margin-right: 4px;
}
.bv-pill {
  background: var(--pill);
  color: var(--primary);
  border-radius: 20px;
  padding: 6px 16px;
  font-size: 1rem;
  font-weight: 500;
  border: none;
  margin-right: 2px;
  cursor: pointer;
  transition: background 0.15s;
}
.bv-pill.active, .bv-pill:focus {
  background: var(--primary);
  color: #fff;
  outline: 2px solid var(--primary);
}
.bv-search-group {
  flex: 1 1 0;
  min-width: 180px;
  display: flex;
  justify-content: flex-end;
}
.bv-search {
  width: 100%;
  max-width: 260px;
  border-radius: 8px;
  border: 1.5px solid var(--border);
  padding: 10px 14px;
  font-size: 1rem;
  color: var(--text);
  background: #fff;
  transition: border 0.15s;
}
.bv-search:focus {
  border-color: var(--primary);
  outline: none;
}

.bv-tag-cloud {
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.bv-tag-label {
  font-weight: 600;
  color: var(--muted);
  margin-right: 4px;
}
.bv-tag {
  background: var(--tag);
  color: var(--tag-text);
  border-radius: 12px;
  padding: 4px 12px;
  font-size: 0.98rem;
  font-weight: 500;
  cursor: pointer;
  margin-right: 2px;
  transition: background 0.15s;
}
.bv-tag:hover, .bv-tag:focus {
  background: var(--primary);
  color: #fff;
  outline: 2px solid var(--primary);
}

.bv-grid {
  display: grid;
  gap: 28px;
  margin-top: 8px;
}

.bv-card {
  background: var(--card);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1.5px solid var(--border);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  transition: box-shadow 0.15s;
}
.bv-card:focus-within, .bv-card:hover {
  box-shadow: 0 4px 24px 0 rgba(0,0,0,0.13);
  border-color: var(--primary);
}
.bv-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px 0 18px;
}
.bv-badge {
  font-size: 0.95rem;
  font-weight: 600;
  color: #fff;
  border-radius: 8px;
  padding: 4px 12px;
  margin-right: 4px;
  background: var(--badge);
}
.bv-pill.asset {
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  border-radius: 8px;
  padding: 4px 12px;
  margin-right: 4px;
}
.bv-thumb-wrap {
  width: 100%;
  aspect-ratio: 16/9;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.bv-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border: none;
  border-radius: 0;
  background: #e5e7eb;
}
.bv-card-body {
  padding: 16px 18px 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.bv-card-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--primary);
  margin-bottom: 2px;
}
.bv-card-desc {
  font-size: 1rem;
  color: var(--muted);
  margin-bottom: 4px;
}
.bv-card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.bv-empty {
  grid-column: 1/-1;
  background: var(--empty);
  border-radius: 16px;
  padding: 48px 0;
  text-align: center;
  color: var(--muted);
}
.bv-empty h2 {
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 8px;
}
.bv-skeleton {
  background: var(--surface);
  border-radius: 16px;
  border: 1.5px solid var(--border);
  padding: 0;
  min-height: 320px;
  display: flex;
  flex-direction: column;
  gap: 0;
}
.bv-thumb-skel {
  width: 100%;
  aspect-ratio: 16/9;
  background: var(--shimmer);
  border-radius: 0;
  margin-bottom: 0;
}
.bv-title-skel {
  height: 22px;
  width: 70%;
  background: var(--shimmer);
  margin: 18px 18px 8px 18px;
  border-radius: 6px;
}
.bv-line-skel {
  height: 14px;
  width: 90%;
  background: var(--shimmer);
  margin: 0 18px 8px 18px;
  border-radius: 6px;
}
.shimmer {
  animation: shimmer 1.2s infinite linear;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
}
@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* Responsive */
@media (max-width: 900px) {
  .bv-grid {
    grid-template-columns: 1fr 1fr !important;
  }
}
@media (max-width: 600px) {
  .bv-hero {
    padding: 18px 0 12px 0;
  }
  .bv-hero-title {
    font-size: 1.3rem;
  }
  .bv-hero-sub, .bv-hero-support {
    font-size: 0.98rem;
  }
  .bv-btn {
    font-size: 1rem;
    padding: 10px 16px;
  }
  .bv-filters {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
  .bv-grid {
    grid-template-columns: 1fr !important;
  }
  .add-footer {
    flex-direction: column;
    gap: 12px;
  }
}
</style>
